type NumericLike = number | string | null | undefined

const toNumber = (value: NumericLike): number => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : 0
  }

  if (typeof value === 'string') {
    const parsed = parseFloat(value)
    return Number.isFinite(parsed) ? parsed : 0
  }

  return 0
}

const clamp = (value: number, min: number, max: number): number =>
  Math.min(Math.max(value, min), max)

const formatPercentageForDisplay = ({
  rawPercentage,
  isZeroAmount,
  isFullAmount
}: {
  rawPercentage: number
  isZeroAmount: boolean
  isFullAmount: boolean
}): number => {
  if (isZeroAmount) {
    return 0
  }

  if (isFullAmount) {
    return 100
  }

  if (!Number.isFinite(rawPercentage) || rawPercentage <= 0) {
    return 1
  }

  if (rawPercentage >= 99.95) {
    return 99
  }

  if (rawPercentage < 1) {
    return 1
  }

  return Math.min(Math.round(rawPercentage), 99)
}

export const calculateProgressPercentages = (amounts: {
  original: NumericLike
  recognized: NumericLike
  remaining: NumericLike
}) => {
  const original = Math.max(toNumber(amounts.original), 0)

  if (original === 0) {
    return { recognized: 0, remaining: 0 }
  }

  const rawRecognized = clamp(toNumber(amounts.recognized), 0, original)
  const rawRemaining = clamp(
    toNumber(amounts.remaining),
    0,
    Math.max(0, original - rawRecognized)
  )

  const recognizedRatio = (rawRecognized / original) * 100
  const remainingRatio = (rawRemaining / original) * 100

  const recognized = formatPercentageForDisplay({
    rawPercentage: recognizedRatio,
    isZeroAmount: rawRecognized === 0,
    isFullAmount: rawRemaining === 0
  })
  const remaining = formatPercentageForDisplay({
    rawPercentage: remainingRatio,
    isZeroAmount: rawRemaining === 0,
    isFullAmount: rawRecognized === 0
  })

  return { recognized, remaining }
}

export const formatProgressPercentage = (
  value: NumericLike,
  original: NumericLike
) => {
  const { recognized } = calculateProgressPercentages({
    original,
    recognized: value,
    remaining: toNumber(original) - toNumber(value)
  })

  return recognized
}
